import type Alpine from "alpinejs";

export type ScreenshotMode = "none" | "capture" | "upload";

export interface Attachment {
  blob: Blob;
  name: string;
  previewUrl: string;
}

export type CaptureStatus = "ok" | "cancelled" | "failed" | "unsupported";

export interface CaptureResult {
  blob: Blob | null;
  status: CaptureStatus;
}

export interface FeedbackBubble {
  open: boolean;
  busy: boolean;
  message: string;
  screenshotMode: ScreenshotMode;
  attachment: Attachment | null;
  attachError: string;
  dragOver: boolean;
  t: Record<string, string>;
  init(): void;
  submit(): Promise<void>;
  collectContext(): Record<string, unknown>;
  captureScreenshot(): Promise<CaptureResult>;
  handleDrop(event: DragEvent): void;
  handleFileSelect(event: Event): void;
  handlePaste(event: ClipboardEvent): void;
  attachFile(file: File): Promise<void>;
  clearAttachment(): void;
  closeModal(): void;
  trapTab(event: KeyboardEvent): void;
}

type DisplayMediaOptions = {
  video: MediaTrackConstraints;
  audio: boolean;
  preferCurrentTab?: boolean;
  selfBrowserSurface?: "include" | "exclude";
  surfaceSwitching?: "include" | "exclude";
};

const enc = new TextEncoder();
function capBytes(s: string, max: number): string {
  const bytes = enc.encode(s);
  if (bytes.byteLength <= max) return s;
  return new TextDecoder().decode(bytes.slice(0, max));
}

function capContextJSON(obj: unknown, max: number): string {
  const s = JSON.stringify(obj);
  if (enc.encode(s).byteLength <= max) return s;
  // Drop the heaviest field first, retry. If still over, fall through to byte-cut.
  const trimmed = JSON.stringify({ ...(obj as object), stores: "[omitted: oversize]" });
  if (enc.encode(trimmed).byteLength <= max) return trimmed;
  return capBytes(trimmed, max);
}

// Mirrors domain.MaxScreenshotBytes in
// internal/feedback/internal/domain/feedback.go, which is the authority — this
// copy exists only so the browser can shrink before wasting an upload.
const MAX_SCREENSHOT_BYTES = 4 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

// Tried in order; the first result under the cap wins. PNG first so small UI
// text stays crisp — JPEG is the fallback that reliably collapses gradient-
// heavy captures. Re-encoding at full scale is skipped: it rarely gains enough
// to matter.
const SHRINK_LADDER: Array<{ scale: number; type: string; quality?: number }> = [
  { scale: 0.75, type: "image/png" },
  { scale: 0.5, type: "image/png" },
  { scale: 0.75, type: "image/jpeg", quality: 0.85 },
];

async function normalizeImage(file: File): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    console.warn("feedback attachment decode failed", e);
    return null;
  }
  try {
    for (const step of SHRINK_LADDER) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * step.scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * step.scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob(res, step.type, step.quality),
      );
      if (blob && blob.size <= MAX_SCREENSHOT_BYTES) return blob;
    }
    return null;
  } finally {
    bitmap.close();
  }
}

function renameForType(name: string, type: string): string {
  const ext = type === "image/png" ? ".png" : type === "image/webp" ? ".webp" : ".jpg";
  const base = name.replace(/\.[^.]+$/, "") || "screenshot";
  return base + ext;
}

function createDisplayMediaOptions(): DisplayMediaOptions {
  return {
    video: {
      displaySurface: "browser",
      frameRate: { ideal: 1, max: 4 },
    },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: "include",
    surfaceSwitching: "exclude",
  };
}

const VIDEO_FRAME_TIMEOUT_MS = 3000;

async function waitForVideoFrame(video: HTMLVideoElement): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error("timed out waiting for capture video frame")),
      VIDEO_FRAME_TIMEOUT_MS,
    );
  });
  const work = (async () => {
    if (video.readyState < 2) {
      await new Promise<void>((resolve, reject) => {
        const onLoaded = () => resolve();
        const onError = () =>
          reject(video.error ?? new Error("video failed to load capture stream"));
        video.addEventListener("loadeddata", onLoaded, { once: true });
        video.addEventListener("error", onError, { once: true });
      });
    }

    await video.play();
    await new Promise<void>((resolve) => {
      if ("requestVideoFrameCallback" in video) {
        (
          video as HTMLVideoElement & {
            requestVideoFrameCallback(cb: () => void): number;
          }
        ).requestVideoFrameCallback(() => resolve());
        return;
      }
      requestAnimationFrame(() => resolve());
    });
  })();
  try {
    await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
    // If the timeout won, work may still reject later; avoid an unhandled rejection.
    work.catch(() => {});
  }
}

export function registerFeedbackBubble(alpine: typeof Alpine): void {
  alpine.data("feedbackBubble", () => ({
    open: false,
    busy: false,
    message: "",
    screenshotMode: "capture" as ScreenshotMode,
    attachment: null as Attachment | null,
    attachError: "",
    dragOver: false,
    t: {} as Record<string, string>,

    // Alpine runs init() with $el = the x-data root, which carries the
    // translated data-* attributes. Handlers later run with $el = the element
    // carrying the directive (e.g. the submit button), so copy them now.
    init(this: FeedbackBubble & { $el: HTMLElement }) {
      this.t = { ...this.$el.dataset } as Record<string, string>;
    },

    async submit(this: FeedbackBubble) {
      if (!this.message.trim()) return;
      this.busy = true;
      try {
        let screenshot: Blob | null = null;
        let captureStatus: CaptureStatus = "ok";
        let screenshotName = "screenshot.jpg";
        if (this.screenshotMode === "capture") {
          const result = await this.captureScreenshot();
          screenshot = result.blob;
          captureStatus = result.status;
        } else if (this.screenshotMode === "upload" && this.attachment) {
          screenshot = this.attachment.blob;
          screenshotName = this.attachment.name;
        }

        const fd = new FormData();
        fd.set("message", this.message);
        fd.set("url", location.href);
        fd.set("viewport", `${innerWidth}x${innerHeight}`);
        fd.set("referrer", document.referrer);
        fd.set("console_log", capBytes(JSON.stringify(window.__feedbackBuffer || []), 64 * 1024));
        fd.set("context_json", capContextJSON(this.collectContext(), 16 * 1024));

        if (screenshot) fd.set("screenshot", screenshot, screenshotName);

        const res = await fetch("feedback", {
          method: "POST",
          body: fd,
          headers: { "X-CSRF-Token": window.csrfToken() },
          credentials: "same-origin",
        });
        if (!res.ok) {
          const e = new Error(`status ${res.status}`);
          (e as Error & { status?: number }).status = res.status;
          throw e;
        }

        this.open = false;
        this.message = "";
        this.clearAttachment();
        this.attachError = "";
        const sentWithoutScreenshot =
          this.screenshotMode === "capture" && captureStatus !== "ok";
        window.__toastHandler?.add?.(
          sentWithoutScreenshot ? this.t.toastSubmittedNoScreenshot : this.t.toastSubmitted,
          "success",
          3000,
        );
      } catch (err) {
        const status = (err as Error & { status?: number }).status;
        const tooLarge = status === 413 || status === 422;
        const msg = tooLarge
          ? (this.t.errorBodyTooLarge ?? this.t.toastFailed)
          : this.t.toastFailed;
        window.__toastHandler?.add?.(msg, "error", 4000);
        console.warn("feedback submit failed", err);
      } finally {
        this.busy = false;
      }
    },

    collectContext(): Record<string, unknown> {
      const stores: Record<string, unknown> = {};
      for (const k of ["toast", "sidebar"]) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          stores[k] = (window.Alpine as any)?.store?.(k) ?? null;
        } catch {
          /* ignore */
        }
      }
      return {
        route: location.pathname,
        stores,
        app_locale: document.documentElement.lang,
        timestamp: new Date().toISOString(),
        request_id: window.__feedbackRequestID,
      };
    },

    handleDrop(this: FeedbackBubble, event: DragEvent) {
      this.dragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) void this.attachFile(file);
    },

    handleFileSelect(this: FeedbackBubble, event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (file) void this.attachFile(file);
      // Reset so re-picking the same file fires change again.
      input.value = "";
    },

    handlePaste(this: FeedbackBubble, event: ClipboardEvent) {
      if (!this.open) return;
      const file = Array.from(event.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith("image/"),
      );
      // No image on the clipboard means this is a normal text paste into the
      // textarea — leave it entirely alone.
      if (!file) return;
      event.preventDefault();
      this.screenshotMode = "upload";
      void this.attachFile(file);
    },

    async attachFile(this: FeedbackBubble, file: File) {
      this.attachError = "";

      // A 0-byte file still reports an image/* type, would pass the type check
      // untouched, and the server would then save the report with no screenshot
      // and a success toast — the user believing their image went through.
      if (file.size === 0) {
        this.attachError = this.t.errorScreenshotUnsupportedType;
        return;
      }

      if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
        this.attachError = this.t.errorScreenshotUnsupportedType;
        return;
      }

      let blob: Blob = file;
      let name = file.name || "screenshot.png";

      if (file.size > MAX_SCREENSHOT_BYTES) {
        const shrunk = await normalizeImage(file);
        if (!shrunk) {
          this.attachError = this.t.errorScreenshotShrinkFailed;
          return;
        }
        blob = shrunk;
        name = renameForType(name, shrunk.type);
      }

      this.clearAttachment();
      this.attachment = { blob, name, previewUrl: URL.createObjectURL(blob) };
    },

    clearAttachment(this: FeedbackBubble) {
      if (this.attachment) URL.revokeObjectURL(this.attachment.previewUrl);
      this.attachment = null;
    },

    // Keeps Tab/Shift+Tab inside the dialog panel (mirrors modal.templ).
    trapTab(this: FeedbackBubble & { $refs: Record<string, HTMLElement> }, event: KeyboardEvent) {
      const panel = this.$refs.panel;
      if (!panel) return;
      const items = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.getAttribute("aria-hidden") !== "true" &&
          (el as HTMLInputElement).type !== "hidden" &&
          el.getClientRects().length > 0,
      );
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },

    closeModal(this: FeedbackBubble) {
      this.open = false;
      this.clearAttachment();
      this.attachError = "";
    },

    async captureScreenshot(this: FeedbackBubble): Promise<CaptureResult> {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        console.warn("feedback screenshot capture unavailable: getDisplayMedia unsupported");
        return { blob: null, status: "unsupported" };
      }

      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia(createDisplayMediaOptions());

        // Close the modal only after the capture permission prompt resolves.
        // getDisplayMedia requires transient user activation from the submit
        // click, so we cannot defer the API call behind animation frames.
        this.open = false;
        await new Promise<void>((res) =>
          requestAnimationFrame(() => requestAnimationFrame(() => res())),
        );

        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await waitForVideoFrame(video);

        const width = video.videoWidth || window.innerWidth;
        const height = video.videoHeight || window.innerHeight;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("failed to create screenshot canvas context");
        }
        ctx.drawImage(video, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((res) =>
          canvas.toBlob(res, "image/jpeg", 0.7),
        );
        return blob ? { blob, status: "ok" } : { blob: null, status: "failed" };
      } catch (e) {
        console.warn("feedback screenshot capture failed", e);
        const cancelled = e instanceof DOMException && e.name === "NotAllowedError";
        return { blob: null, status: cancelled ? "cancelled" : "failed" };
      } finally {
        stream?.getTracks().forEach((track) => track.stop());
      }
    },
  } satisfies FeedbackBubble));
}
